import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isMedicalStudentProvider } from '../../utils/providerRegistrationRole';

const STUDENT_HOME = '/provider/student';
const PROVIDER_ALIASES = new Set([
  '/profile',
  '/profile-new',
  '/vault',
  '/verification',
  '/invites',
  '/notifications',
  '/settings',
  '/discover',
]);

export function isPracticingProviderPath(pathname) {
  if (pathname === STUDENT_HOME || pathname.startsWith(`${STUDENT_HOME}/`)) {
    return false;
  }
  return (
    pathname === '/provider' ||
    pathname.startsWith('/provider/') ||
    pathname.startsWith('/negotiations/') ||
    PROVIDER_ALIASES.has(pathname)
  );
}

/**
 * A medical-student account has the provider auth role for compatibility, but
 * it is not a practicing-provider profile. Keep every provider work, matching,
 * credential, service, and sharing surface behind one stage-aware boundary.
 */
export default function MedicalStudentAccessGate({ children }) {
  const { auth, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const isStudent = isMedicalStudentProvider(auth?.user);
  const isStudentHome =
    location.pathname === STUDENT_HOME ||
    location.pathname.startsWith(`${STUDENT_HOME}/`);

  if (
    isLoading &&
    (isStudentHome || isPracticingProviderPath(location.pathname))
  ) {
    return <div>Loading your account…</div>;
  }

  if (isAuthenticated && isStudentHome && !isStudent) {
    // During direct signup, navigation can win the race with /auth/me by a
    // render. The route-state proof is set only by the just-completed student
    // registration request; ordinary providers cannot reach this branch.
    if (location.state?.medicalStudentRegistration === true) return children;
    return <Navigate to="/provider/dashboard" replace />;
  }

  if (
    isAuthenticated &&
    isStudent &&
    isPracticingProviderPath(location.pathname)
  ) {
    return <Navigate to={STUDENT_HOME} replace />;
  }

  return children;
}
